import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Column } from 'primereact/column';
import DeviceApi from "../../services/DeviceApi";
import IApiDevicesResponse from "../../interfaces/IApiDevicesResponse";
import StationApi from "../../services/StationApi";
import { useToast } from "../../context/ToastContext";
import { useTranslation } from 'react-i18next';

// Design System
import PageHeader from "../../components/PageHeader/PageHeader";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import DataTable from "../../components/DataTable/DataTable";
import Input from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { IconButton } from "../../components/IconButton/IconButton";
import type { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";
import Modal from "../../components/Modal/Modal";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const Devices: React.FC = () => {
  const [devices, setDevices] = useState<IApiDevicesResponse[]>([]);
  const [filteredDevices, setFilteredDevices] = useState<IApiDevicesResponse[]>([]);
  const [initialData, setInitialData] = useState<IApiDevicesResponse[]>([]);
  const [inputValueNome, setInputValueNome] = useState<string | null>(null);
  const [inputValueEstacao, setInputValueEstacao] = useState<string | null>(null);
  const [inputValueDescricao, setInputValueDescricao] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [stations, setStations] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [station, setStation] = useState("");
  const [selectedDevice, setSelectedDevice] = useState<any | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<any | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const { displayToast } = useToast();
  const { t: translateMessage } = useTranslation("messages");
  const { t: translateForm } = useTranslation("form");
  const { t: translateGeneral } = useTranslation("general");

  // Pagination/Sort state
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [sortField, setSortField] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const isDirty = useMemo(() => {
    if (editMode && selectedDevice) {
      return name !== selectedDevice.name || description !== selectedDevice.description || station !== selectedDevice.stationId;
    }
    return name !== "" || description !== "" || station !== "";
  }, [name, description, station, editMode, selectedDevice]);

  useEffect(() => {
    fetchDevices();
    fetchStations();
  }, []);

  const fetchDevices = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await DeviceApi.GetAllDevices();
      const fetchedDevices = response.data || [];
      setDevices(fetchedDevices);
      setFilteredDevices(fetchedDevices);
      setInitialData(fetchedDevices);
    } catch (error) {
      console.error("Erro ao buscar devices: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStations = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await StationApi.GetAllStations();
      const stationsMap = response.data
        .map((station: any) => ({
          value: station.id,
          label: station.name,
        }))
        .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label));

      setStations(stationsMap);
    } catch (error) {
      console.error("Erro ao buscar estações: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteDevice = async (device: any): Promise<void> => {
    setIsLoading(true);
    try {
      await DeviceApi.DeleteDevice(device.id);
      displayToast("success", translateMessage("deleteSuccess"), "", false, 3000);
      fetchDevices();
    } catch (error) {
      console.error("Erro ao deletar device: ", error);
      displayToast("error", translateMessage("error"), String(error), false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const addOrUpdateDevice = async (): Promise<void> => {
    let response: any = null;
  
    if (name.trim().toLowerCase() === description.trim().toLowerCase()) {
      displayToast("error", "Nome e descrição não podem ser iguais.", "", false, 3000);
      return;
    }
  
    const deviceExists = devices.some(device => 
      (device.name.toLowerCase() === name.toLowerCase() || device.description.toLowerCase() === description.toLowerCase()) &&
      (!editMode || device.id !== selectedDevice?.id)
    );
  
    if (deviceExists) {
      displayToast("error", translateMessage("duplicateDeviceError"), "", false, 3000);
      return;
    }
  
    try {
      if (editMode && selectedDevice) {
        response = await DeviceApi.UpdateDevice(selectedDevice.id, station, description, name);
      } else {
        response = await DeviceApi.CreateDevice(station, description, name);
      }
  
      if (response.data || response.success) {
        displayToast("success", editMode ? translateMessage("editSuccess") : translateMessage("registerSuccess"), "", false, 3000);
        setVisible(false);
        fetchDevices();
      }
    } catch (error) {
      console.error('Failed: ', error);
      displayToast("error", "Erro ao cadastrar: ", "", false, 3000);
    }
  };

  const openEditModal = (device: any): void => {
    setSelectedDevice(device);
    setStation(device.stationId);
    setName(device.name);
    setDescription(device.description);
    setEditMode(true);
    setVisible(true);
  };

  const openCreateModal = (): void => {
    setSelectedDevice(null);
    setStation("");
    setName("");
    setDescription("");
    setEditMode(false);
    setVisible(true);
  };

  const handleCancel = useCallback(() => {
    if (isDirty) {
      setShowCancelConfirm(true);
    } else {
      setVisible(false);
    }
  }, [isDirty]);

  const resetFilters = (): void => {
    setDevices(initialData);
    setFilteredDevices(initialData);
    setInputValueNome(null);
    setInputValueEstacao(null)
    setInputValueDescricao(null)
    setPage(0);
  };

  const handlePageChange = (e: PaginatorPageChangeEvent): void => {
    setPage(e.first);
    setRows(e.rows);
  };

  const handleSortChange = (e: any): void => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder === 1 ? "asc" : "desc");
  };

  const filterDevices = (): void => {
    let filtered = devices;

    if (inputValueNome) {
      filtered = filtered.filter((event: IApiDevicesResponse): boolean => {
        return event.name?.toLowerCase().includes(inputValueNome.toLowerCase());
      });
    }

    if (inputValueEstacao) {
      filtered = filtered.filter((event: IApiDevicesResponse): boolean => {
        return event.station.name.toLowerCase().includes(inputValueEstacao.toLowerCase());
      });
    }

    if (inputValueDescricao) {
      filtered = filtered.filter((event: IApiDevicesResponse): boolean => {
        return event.description?.trim().toLowerCase().includes(inputValueDescricao.trim().toLowerCase());
      });
    }

    setFilteredDevices(filtered);
    setPage(0);
  };

  const confirmDelete = (device: any): void => {
    setDeviceToDelete(device);
    setShowDeleteConfirm(true);
  };

  return (
    <div className="overflow-hidden">
      <PageHeader
        title={translateForm("devices")}
        description="Gerencie os dispositivos físicos do sistema"
      >
        <Button
          onClick={openCreateModal}
          label={translateGeneral("register")}
          icon="add"
          severity="primary"
        />
      </PageHeader>

      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <FilterPanel
          title="Filtros"
          onApply={filterDevices}
          onClear={resetFilters}
        >
          <Input
            type="text"
            label={translateForm("name")}
            value={inputValueNome || ""}
            onChange={(val: any) => setInputValueNome(val)}
          />
          <Input
            type="text"
            label={translateForm("description")}
            value={inputValueDescricao || ""}
            onChange={(val: any) => setInputValueDescricao(val)}
          />
          <Input
            type="text"
            label={translateForm("station")}
            value={inputValueEstacao || ""}
            onChange={(val: any) => setInputValueEstacao(val)}
          />
        </FilterPanel>

        {isLoading ? (
          <div>{translateGeneral("loading")}</div>
        ) : (
          <DataTable
            value={filteredDevices}
            paginated
            first={page}
            rows={rows}
            totalRecords={filteredDevices.length}
            onPageChange={handlePageChange}
            rowsPerPageOptions={[10, 20, 50]}
            loading={isLoading}
            sortField={sortField}
            sortOrder={sortOrder === "asc" ? 1 : -1}
            onSort={handleSortChange}
            emptyMessage={translateGeneral("noRecordsFound")}
          >
            <Column
              field="name"
              header={translateForm("name")}
              sortable
            />
            <Column
              field="description"
              header={translateForm("description")}
              sortable
            />
            <Column
              field="station.name"
              header={translateForm("station")}
              sortable
            />
            <Column
              field="actions"
              header={translateGeneral("actions")}
              body={(rowData: IApiDevicesResponse) => (
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <IconButton
                    icon="edit"
                    variant="text"
                    severity="secondary"
                    onClick={() => openEditModal(rowData)}
                    aria-label={translateGeneral("edit")}
                  />
                  <IconButton
                    icon="delete"
                    variant="text"
                    severity="secondary"
                    onClick={() => confirmDelete(rowData)}
                    aria-label={translateGeneral("delete")}
                  />
                </div>
              )}
              style={{ width: "8rem" }}
            />
          </DataTable>
        )}

        <Modal
          header={editMode ? translateGeneral("editDevice") : translateGeneral("newDevice")}
          visible={visible}
          onHide={handleCancel}
          size="md"
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <Button
                onClick={handleCancel}
                label={translateGeneral("cancel")}
                severity="secondary"
                variant="text"
              />
              <Button
                onClick={addOrUpdateDevice}
                label={translateGeneral("save")}
                icon="save"
                severity="primary"
              />
            </div>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem", paddingTop: "0.5rem" }}>
            <Input
              type="text"
              label={translateForm("name")}
              required
              value={name}
              onChange={(val: any) => setName(val)}
            />
            <Input
              type="text"
              label={translateForm("description")}
              required
              value={description}
              onChange={(val: any) => setDescription(val)}
            />
            <Input
              type="select"
              label={translateForm("station")}
              value={station}
              options={stations}
              onChange={(val: any) => setStation(val)}
              required
            />
          </div>
        </Modal>

        <ConfirmModal
          visible={showDeleteConfirm}
          onHide={() => setShowDeleteConfirm(false)}
          onConfirm={() => {
            if (deviceToDelete) deleteDevice(deviceToDelete);
            setShowDeleteConfirm(false);
          }}
          title={translateGeneral("confirmation")}
          message={translateMessage("deleteDevice")}
        />

        <ConfirmModal
          visible={showCancelConfirm}
          onHide={() => setShowCancelConfirm(false)}
          onConfirm={() => {
            setShowCancelConfirm(false);
            setVisible(false);
          }}
        />
      </div>
    </div>
  );
};

export default Devices;
